/**
 * `FX-3`, `M3` (`S2-4`): **where an invitation link LANDS, with one author.**
 *
 * ## The defect this exists to prevent, which already happened
 *
 * The invitation mail builds `${base}/invitation/${token}` and says, in its own comment, *« The path is the
 * SAME on every surface, and deliberately: the screen is one screen, mounted by each app at
 * `/invitation/:token`, so only the host differs between a dealership's link and a bank's. »*
 *
 * `apps/bank-web` matched `/invitations/` , **plural** , so a bank's invitation fell through to the login
 * page, and that is the whole of `S2-4`: *« Bank invitation link lands on the login page, never on password
 * creation; HB Bank and Hbank stay "Invitation envoyée", 0 users »*. Two banks stood unopenable for a week,
 * the dealer's and the cabinet's links worked, and nothing in the repository was red , the mail's path and
 * the app's route were two strings that nobody had ever compared.
 *
 * **A path with two authors has no author.** So it is declared here, once, and the mail and every app's
 * router read it. `invitation-path.spec.ts` walks the repository for the plural form and for a hand-written
 * copy, which is the shape `P0-ITG-003`'s signer took after the same class of failure cost 34 assertions.
 *
 * ## Why `@hakili/contracts` and not a shared UI package
 *
 * It is a fact on the WIRE: the api writes the link into an email and four applications resolve it. That is
 * exactly what this package is for , the api and every app already import it , and 9.3.1's boundaries keep
 * it free of any module's domain, which a path string is.
 */

/** The one segment, with no slashes, so a caller cannot double one. */
export const INVITATION_PATH_SEGMENT = "invitation";

/** The path an invitation link carries, host apart: `/invitation/{token}`. */
export function invitationPathFor(token: string): string {
  return `/${INVITATION_PATH_SEGMENT}/${token}`;
}

/** The route pattern an application's table declares for it. */
export const INVITATION_ROUTE_PATTERN = `/${INVITATION_PATH_SEGMENT}/:token`;

/**
 * The token a pathname carries, or `null`.
 *
 * **Anchored at the start and stopping at the first `/`, `?` or `#`**, which is what the four apps' own
 * matchers did: a token is 32 random bytes in hex and carries none of those, so anything after one is not
 * part of it. Exported so an app resolves the link through this function instead of writing the regular
 * expression a fifth time , the fifth copy being the one that says `invitations`.
 */
export function invitationTokenFrom(pathname: string): string | null {
  const found = new RegExp(`^/${INVITATION_PATH_SEGMENT}/([^/?#]+)`, "u").exec(pathname);
  return found?.[1] ?? null;
}
